import { distance, point, center as turfCenter } from "@turf/turf"
import type { IParcel, POI, GouvCommuneInfos } from "@/types/explore"
import { geoFetch } from "./geo-fetch"

interface OverpassElement {
  type: string
  id: number | string
  lat?: number
  lon?: number
  tags?: Record<string, string>
  nodes?: (number | string)[]
  [key: string]: unknown
}

export const getIGNParcelFromCoordinates = async (coordinates: [number, number]): Promise<IParcel | undefined> => {
  try {
    const res = await fetch(`/api/parcels/at_point?lng=${coordinates[0]}&lat=${coordinates[1]}`)
    if (!res.ok) return undefined
    return await res.json()
  } catch {
    return undefined
  }
}

export const searchParcelNumber = async (
  codeInsee?: string,
  parcelSection?: string,
  parcelNumber?: string,
): Promise<(IParcel & { bbox: [number, number, number, number] })[]> => {
  try {
    const params = new URLSearchParams()
    if (codeInsee) params.set("code_insee", codeInsee)
    if (parcelSection) params.set("section", parcelSection.padStart(2, "0"))
    if (parcelNumber) params.set("numero", parcelNumber.padStart(4, "0"))
    const res = await fetch(`/api/parcels/search?${params.toString()}`)
    if (!res.ok) return []
    const data = await res.json()
    const features = data?.features
    if (!Array.isArray(features)) return []
    return features.map((f: Record<string, unknown>) => ({
      id: f.id as string,
      properties: f.properties as IParcel["properties"],
      geometry: f.geometry as IParcel["geometry"],
      bbox: f.bbox as [number, number, number, number],
    }))
  } catch {
    return []
  }
}

export const getAddressDetails = async (
  lng: number,
  lat: number,
): Promise<{
  city: string | null
  street: string | null
  label: string | null
  name: string | null
  houseNumber: string | null
}> => {
  try {
    const res = await geoFetch(`https://data.geopf.fr/geocodage/reverse/?lon=${lng}&lat=${lat}`)
    const data = await res.json()
    const feature = data.features?.[0]
    if (!feature) {
      return await fallbackMapboxGeocode(lng, lat)
    }
    const { city, street, label, name, houseNumber } = feature.properties
    return { city, street: street || null, label, name, houseNumber }
  } catch {
    return await fallbackMapboxGeocode(lng, lat)
  }
}

async function fallbackMapboxGeocode(
  lng: number,
  lat: number,
): Promise<{
  city: string | null
  street: string | null
  label: string | null
  name: string | null
  houseNumber: string | null
}> {
  const empty = { city: null, street: null, label: null, name: null, houseNumber: null }
  try {
    const token = import.meta.env.VITE_MAPBOX_TOKEN || ""
    const res = await geoFetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}`,
    )
    const data = await res.json()
    const mbFeature = data.features?.[0]
    if (!mbFeature) return empty
    const context = mbFeature.context || []
    const cityObj =
      context.find((c: { id: string }) => c.id.startsWith("place")) ||
      context.find((c: { id: string }) => c.id.startsWith("locality"))
    return {
      city: cityObj ? cityObj.text : null,
      street: mbFeature.text || null,
      label: mbFeature.place_name || null,
      name: mbFeature.text || null,
      houseNumber: mbFeature.address || null,
    }
  } catch {
    return empty
  }
}

export const getNearbyPOIs = async (lat: number, lon: number): Promise<POI[]> => {
  try {
    const query = `
      [out:json];
      (
        nwr["amenity"="school"](around:1000,${lat},${lon});
        nwr["railway"="station"](around:1000,${lat},${lon});
        nwr["railway"="tram_stop"](around:1000,${lat},${lon});
        nwr["amenity"="hospital"](around:1000,${lat},${lon});
        nwr["amenity"="doctors"](around:1000,${lat},${lon});
        nwr["amenity"="pharmacy"](around:1000,${lat},${lon});
        nwr["amenity"="townhall"](around:1000,${lat},${lon});
        nwr["amenity"="post_office"](around:1000,${lat},${lon});
        nwr["shop"="supermarket"](around:1000,${lat},${lon});
        nwr["shop"="convenience"](around:1000,${lat},${lon});
        nwr["shop"="bakery"](around:1000,${lat},${lon});
      );
      out body;
      >;
      out skel qt;
    `
    const res = await geoFetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`)
    const data = await res.json()
    const elements: OverpassElement[] | undefined = data.elements
    if (!elements) return []

    const poiNodes = elements.filter((el) => el.type === "node" && el?.tags)
    const polygons = elements
      .filter((el) => el.type === "way")
      .map((way: OverpassElement) => {
        const coordinates = (way.nodes ?? []).map((nodeId: number | string) => {
          const node = elements.find((el) => el.id === nodeId)
          return [node?.lon ?? 0, node?.lat ?? 0]
        })
        const center = turfCenter({
          type: "Feature",
          properties: {},
          geometry: { type: "Polygon", coordinates: [coordinates] as [number, number][][] },
        })
        return {
          ...way,
          id: way.id,
          coordinates,
          lat: center.geometry.coordinates[1],
          lon: center.geometry.coordinates[0],
        }
      })

    const poisWithDistance: POI[] = ([...poiNodes, ...polygons] as POI[]).map((poi) => ({
      ...poi,
      distance:
        poi.lon !== undefined && poi.lat !== undefined
          ? distance(point([lon, lat]), point([poi.lon!, poi.lat!]), { units: "meters" })
          : undefined,
    }))

    return poisWithDistance
  } catch (error) {
    console.error("Error fetching POIs:", error)
    return []
  }
}

export const getCommuneHistoricMonuments = async (
  codeInsee: string,
  codeDep: string,
  lat: number,
  lon: number,
): Promise<POI[]> => {
  try {
    const res = await geoFetch(
      `https://tabular-api.data.gouv.fr/api/resources/3a52af4a-f9da-4dcc-8110-b07774dfb3bc/data/?Departement_format_numerique__exact=${codeDep}&COG_Insee_lors_de_la_protection__exact=${codeInsee}`,
    )
    const data = await res.json()
    return (data?.data || []).map((d: Record<string, string>) => {
      const coordinates = d.coordonnees_au_format_WGS84.split(",")
      const hmLat = parseFloat(coordinates[0])
      const hmLon = parseFloat(coordinates[1])
      const hm: POI = {
        id: d.__id,
        lat: hmLat,
        lon: hmLon,
        tags: { name: d.Titre_editorial_de_la_notice, amenity: "monument" },
        distance: distance(point([lon, lat]), point([hmLon, hmLat]), { units: "meters" }),
      }
      return hm
    })
  } catch {
    return []
  }
}

export const getClosestPublicStations = async (
  lat: number,
  lon: number,
  radiusMeters: number = 10000,
): Promise<POI[] | null> => {
  try {
    const query = `
      [out:json];
      (
        nwr["railway"="station"](around:${radiusMeters},${lat},${lon});
        nwr["railway"="tram_stop"](around:${radiusMeters},${lat},${lon});
        nwr["station"="subway"](around:${radiusMeters},${lat},${lon});
        nwr["public_transport"="station"]["train"="yes"](around:${radiusMeters},${lat},${lon});
      );
      out body;
      >;
      out skel qt;
    `
    const res = await geoFetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`)
    if (!res.ok) throw new Error(`Overpass API request failed with status ${res.status}`)
    const data = await res.json()
    const elements: OverpassElement[] | undefined = data.elements
    if (!elements) return null

    const stations = elements.filter((el) => {
      if (el.type !== "node" || !el?.tags) return false
      if (
        el.tags.railway === "station" &&
        (el.tags["SNCF:stop_name"] ||
          el.tags.operator === "SNCF" ||
          el.tags.operator === "SNCF Gares & Connexions" ||
          el.tags.train === "yes" ||
          !el.tags.light_rail)
      )
        return true
      if (el.tags.railway === "tram_stop" || (el.tags.public_transport === "stop_position" && el.tags.tram === "yes"))
        return true
      if (
        el.tags.station === "subway" ||
        el.tags.railway === "subway_entrance" ||
        (el.tags.public_transport === "station" && el.tags.subway === "yes") ||
        (el.tags.railway === "station" && el.tags.subway === "yes")
      )
        return true
      return false
    })

    if (stations.length === 0) return null

    const stationsWithDistance = stations.map((station: OverpassElement) => {
      const tags = station.tags!
      let transportType: "bus" | "train" | "tram" | "subway" | "rer" = "train"
      if (tags.railway === "tram_stop" || tags.tram === "yes") {
        transportType = "tram"
      } else if (tags.station === "subway" || tags.subway === "yes") {
        transportType = "subway"
      } else if (tags.ref?.startsWith("RER") || tags.network === "RER") {
        transportType = "rer"
      }

      return {
        id: station.id,
        lat: station.lat!,
        lon: station.lon!,
        tags: {
          name: tags.name || "Station inconnue",
          railway: tags.railway,
          operator: tags.operator,
          amenity: transportType === "tram" ? "tram_stop" : transportType === "subway" ? "subway" : "railway_station",
        },
        transportType,
        distance: distance(point([lon, lat]), point([station.lon!, station.lat!]), { units: "meters" }),
      }
    })

    stationsWithDistance.sort((a, b) => a.distance - b.distance)
    return stationsWithDistance
  } catch {
    return null
  }
}

export const searchCommune = async (
  fieldValue: string,
  inputValue: string,
  fields?: string,
  limit: number = 5,
  format: string = "json",
  geometry: string = "centre",
): Promise<GouvCommuneInfos[]> => {
  try {
    const res = await geoFetch(
      `https://geo.api.gouv.fr/communes?${fieldValue}=${inputValue}&fields=${fields}&boost=population&limit=${limit}&format=${format}&geometry=${geometry}`,
    )
    return await res.json()
  } catch {
    return []
  }
}

export const getTerrainElevation = async (lon: number, lat: number): Promise<number | null> => {
  try {
    const res = await geoFetch(
      `https://data.geopf.fr/altimetrie/1.0/calcul/alti/rest/elevation.json?lon=${lon}&lat=${lat}&resource=ign_rge_alti_wld&zonly=true`,
    )
    const data = await res.json()
    const elevation = data?.elevations?.[0]
    return typeof elevation === "number" && elevation !== -99999 ? elevation : null
  } catch {
    return null
  }
}

export const getCommuneFromCoordinates = async (
  lat: number,
  lon: number,
): Promise<{ code: string; nom: string; codeDepartement: string } | null> => {
  try {
    const res = await fetch(`/api/explore/commune_from_coordinates?lat=${lat}&lon=${lon}`)
    if (!res.ok) return null
    return res.json()
  } catch {
    return null
  }
}

export const getCommuneINSEECode = async (nom: string, codePostal: string): Promise<string | null> => {
  try {
    const res = await geoFetch(
      `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(nom)}&codePostal=${codePostal}&fields=code,nom,codesPostaux&limit=1`,
    )
    const data = await res.json()
    return data?.[0]?.code || null
  } catch {
    return null
  }
}

export const fetchCommuneContour = async (code: string): Promise<{ type: string; coordinates: unknown[] } | null> => {
  try {
    const res = await fetch(`/api/explore/commune_contour/${code}`)
    if (!res.ok) return null
    const data = await res.json()
    return data?.contour || null
  } catch {
    return null
  }
}
